import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, TicketStatus, TicketStatusId, TicketViewModel } from '../../../models';
import { computeDueInDays } from '../computeDueInDays';
import { IAuthentication, IAuthenticationService, ILoggerService, IticketColoringService } from '../../../services';
import { guidEmpty, Roles } from '../../../common/';

class TicketController implements angular.IComponentController {
    authentication: IAuthentication;
    classed: { [clasName: string]: boolean };
    canCloseOrReopen: boolean;
    currentUserGuid: string;
    dueInDays: string;
    hasAnyChild: boolean;
    isLoading: boolean;
    loadingMessage: string;
    numberOfToDo: number;
    numberOfInProgress: number;
    numberOfDone: number;
    saveTicket: ({ ticket: Ticket }) => angular.IPromise<void>;
    selectedTicketViewModel: TicketViewModel;
    ticketViewModel: TicketViewModel;
    ticketStatusList: TicketStatus[];

    static $inject = [
        '$state',
        '$translate',
        'ticketColoringService',
        'loggerService'
    ];

    constructor(
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _ticketColoringService: IticketColoringService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        if (this.ticketViewModel) {
            this.initializeAndCompute(this.ticketViewModel);
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['ticketViewModel'] && changesObj['ticketViewModel'].currentValue) {
            const ticketViewModel = changesObj['ticketViewModel'].currentValue as TicketViewModel;
            this.initializeAndCompute(ticketViewModel);
            this.classed = this.getClass(ticketViewModel);
            const isMaster = this.userInRole([Roles.master]);
            this.canCloseOrReopen = (this.ticketViewModel.model.statusId === TicketStatusId.Completed
                || this.ticketViewModel.model.statusId === TicketStatusId.Rejected)
                && (this.currentUserGuid === this.ticketViewModel.model.creationUser
                    || isMaster);
        }
    }

    select(): void {
        this.selectedTicketViewModel = this.ticketViewModel;
    }

    onDragStart(object: string) {
        this.select();
    }

    close($event: angular.IAngularEvent) {
        this.setStatus($event, TicketStatusId.Closed, 'Ticket has been closed');
    }

    reopen($event: angular.IAngularEvent) {
        const statusId = this.ticketViewModel.model.assignedUser !== guidEmpty
            ? TicketStatusId.Assigned
            : TicketStatusId.New;
        this.setStatus($event, statusId, 'Ticket has been reopened');
    }

    openActionItem(id: number, e: angular.IAngularEvent) {
        this._$state.go('ticketing.actionItems', { preFilteredId: id }, { reload: true });
        e.stopPropagation();
    }

    private initializeAndCompute(ticketViewModel: TicketViewModel) {
        this.computeNumberOfChildren(ticketViewModel);
        this.computeDueInDays(ticketViewModel);
    }

    private computeNumberOfChildren(vm: TicketViewModel): void {
        const counts = vm.model.childrenCountByStatus;
        if (counts) {
            this.numberOfToDo = counts[TicketStatusId.New] ?? 0;
            this.numberOfInProgress = counts[TicketStatusId.Assigned] ?? 0;
            this.numberOfDone = (counts[TicketStatusId.Closed] ?? 0)
                + (counts[TicketStatusId.Completed] ?? 0);
            this.hasAnyChild = (this.numberOfDone + this.numberOfInProgress + this.numberOfToDo) > 0;
        }
    }

    private computeDueInDays(ticketViewModel: TicketViewModel): void {
        this.dueInDays = computeDueInDays(ticketViewModel, this._$translate);
    }

    private setStatus($event: angular.IAngularEvent,
        statusId: TicketStatusId,
        message: string): angular.IPromise<void> | null {
        $event.preventDefault();
        if ($event.stopPropagation) {
            $event.stopPropagation();
        }
        if (this.saveTicket) {
            const { model } = this.ticketViewModel;
            model.statusId = statusId;
            return this.saveTicket({ ticket: model })
                .then(() => {
                    this._loggerService.logSuccess(this._$translate.instant(message));
                });
        }
        return null;
    }

    private getClass(vm: TicketViewModel): { [className: string]: boolean } {
        return this._ticketColoringService.getClass(vm);
    }

    // Check if user has at least an allowed role
    private userInRole(roles: string[]): boolean {
        return _.some(this.authentication.roles, r => roles.indexOf(r) >= 0);
    }
}

export const wtTicket: ComponentDefinition = {
    componentId: 'wtTicket',
    controller: TicketController,
    template: require('./ticket.component.html'),
    controllerAs: 'vm',
    bindings: {
        authentication: '<',
        currentUserGuid: '<?',
        ticketViewModel: '<',
        selectedTicketViewModel: '=',
        ticketStatusList: '<',
        dragDisabled: '<?',
        saveTicket: '&?'
    }
};